import { InputType, SegmentationOptionsFactory } from '@salecycle/experimental/Modal/SegmentationOptionsFactory';
import { ISelectOption, ISelectOptionWithChildren } from '@salecycle/ui-component-library';
import { SegmentationOptions } from './SegmentationOptions';
import { allCountries } from 'models/utils/countries';

export class OnsiteSegmentationOptions implements SegmentationOptionsFactory {
  public ruleTypeOptions: ISelectOptionWithChildren[];

  public constructor(private t: any) {
    this.ruleTypeOptions = this.parseOptions(SegmentationOptions);
  }

  private parseOptions(options): ISelectOptionWithChildren[] {
    return options.map(option => ({
      value: option.category,
      label: this.t(`onsite.segmentationRules.section.${option.category}`),
      children: option.children && Array.isArray(option.children) ? this.parseOptions(option.children) : null,
    }));
  }

  private findProperty(options, selectedRule: string, findOperators: boolean) {
    for (const option of options) {
      if (option.category === selectedRule) {
        return findOperators ? option.operators || [] : option.values || [];
      } else if (option.children) {
        const values = this.findProperty(option.children, selectedRule, findOperators);
        if (values.length > 0) {
          return values;
        }
      }
    }
    return [];
  }

  private getDefaultProperty(options, selectedRule: string, findOperators: boolean) {
    for (const option of options) {
      if (option.category === selectedRule) {
        return findOperators ? option.defaultOperator || [] : option.defaultValue || [];
      } else if (option.children) {
        const defaults = this.getDefaultProperty(option.children, selectedRule, findOperators);
        if (defaults.length > 0) {
          return defaults;
        }
      }
    }
    return [];
  }

  private createSelectOptions(options: string[], isCondition: boolean, selectedRule?: string): ISelectOption<string>[] {
    return options.map(option => ({
      value: option,
      label: isCondition
        ? this.t(`onsite.segmentationRules.conditions.${option}`)
        : selectedRule === 'geoLocation' || selectedRule === 'shippingCountry'
        ? allCountries[option]
        : this.t(`onsite.segmentationRules.values.${option}`),
    }));
  }

  public ruleOptionCreator(selectedRule: string): ISelectOptionWithChildren | undefined {
    if (!selectedRule) {
      return;
    }

    return {
      value: selectedRule,
      label: this.t(`onsite.segmentationRules.types.${selectedRule}`),
      children: null,
    };
  }

  public operatorOptionCreator(selectedCondition: string): ISelectOption<string> | undefined {
    if (!selectedCondition) {
      return;
    }

    return {
      value: selectedCondition,
      label: this.t(`onsite.segmentationRules.conditions.${selectedCondition}`),
    };
  }

  public valueOptionCreator(rule: string, value: string): ISelectOption<string> | undefined {
    if (!rule) {
      return;
    }

    return {
      value: rule,
      label:
        rule === 'geoLocation' || rule === 'shippingCountry'
          ? allCountries[value]
          : this.t(`onsite.segmentationRules.values.${value}`),
    };
  }

  public inputTypeSelector(selectedRule: string): InputType {
    const stringRules = [
      'url',
      'basketSource',
      'basketType',
      'repopulationUrl',
      'itemName',
      'itemUrl',
      'itemId',
      'itemCategory',
      'legacy',
    ];

    const numericRules = ['basketValue'];

    return stringRules.includes(selectedRule) ? 'string' : numericRules.includes(selectedRule) ? 'number' : undefined;
  }

  public operatorsSelector(selectedRule: string): ISelectOption<string>[] {
    const segmentationOperators = this.findProperty(SegmentationOptions, selectedRule, true);
    return this.createSelectOptions(segmentationOperators, true);
  }

  public valuesSelector(selectedRule: string): ISelectOption<string>[] {
    const ruleOptions = this.findProperty(SegmentationOptions, selectedRule, false);
    return this.createSelectOptions(ruleOptions, false, selectedRule);
  }

  public defaultValuesSelector(selectedRule: string) {
    const defaultOperator = this.getDefaultProperty(SegmentationOptions, selectedRule, true);
    const defaultValues = this.getDefaultProperty(SegmentationOptions, selectedRule, false);

    return {
      operator: {
        value: defaultOperator.length > 0 ? defaultOperator : undefined,
        label:
          defaultOperator.length > 0 ? this.t(`onsite.segmentationRules.conditions.${defaultOperator}`) : undefined,
      },
      value: {
        value: defaultValues.length > 0 ? defaultValues : undefined,
        label: defaultValues.length > 0 ? this.t(`onsite.segmentationRules.values.${defaultValues}`) : undefined,
      },
    };
  }
}
